/* ============================================================================
   Sportstech-Main — Reviews.io Full Custom Renderer
   ----------------------------------------------------------------------------
   - Holt alle Reviews per Reviews.io API (paginiert)
   - Rendert komplette eigene Section: Title, Hero, Gallery, Toolbar, Cards, LoadMore
   - Sort + Filter + Search client-side
   - Photo-Lightbox
   - Reviews.io Widget wird komplett ausgeblendet (CSS)
   - Kein Avatar, kein Write-Review-Button (per User-Request)
   ============================================================================ */
(function () {
  'use strict';

  const CONFIG = window.STREVIEWS_CONFIG || {};
  const STORE = CONFIG.storeId || '12tgj7-ah.myshopify.com';
  const LOCALE = CONFIG.locale || (document.documentElement.lang || 'de').split('-')[0];
  const I18N = CONFIG.i18n || {};
  const API_BASE = 'https://api.reviews.io/product/review';
  const PER_PAGE = 100;
  const INITIAL_VISIBLE = 5;
  const BATCH_LOAD = 5;
  // ST-FEAT 2026-08-12 (Wrike 4528640846): Foto-Reviews werden priorisiert angezeigt.
  // Reviews.io kennt den Server-Filter `photos=true` — der liefert AUSSCHLIESSLICH
  // Reviews mit Bild und ignoriert die normale Seiten-Reihenfolge. Ohne diesen
  // Extra-Call sieht der Initial-Render nur die Foto-Reviews, die zufällig in den
  // neuesten 100 stecken (F37s: 10 von 65) — zu wenig zum Priorisieren.
  // Eine Seite reicht: reichstes Produkt hat 65 Foto-Reviews (Audit 2026-08-12).
  // Der Rest kommt ohnehin über die normale Paginierung nach.
  const PHOTO_PER_PAGE = 100;
  // ST 2026-10-27 (Wrike 4592398622, Ali): kein Rating-Floor mehr. Liste, Anzahl, Durchschnitt,
  // Balken, Sortierung und Filter nutzen alle Bewertungen 1-5★ (vorher MIN_VISIBLE_RATING = 3).
  // ST-FEAT 2026-09-30 (Wrike 4575801422): Reviews.io "Translate Reviews & Replies" (Plus-Plan)
  // ist aktiv. `/product/review` ignoriert `lang` weiterhin und liefert nur Deutsch; übersetzt
  // liefert nur `timeline/data` (Endpoint der offiziellen Widgets): `comments` + `review_title`
  // übersetzt, `original_comments` = Original, `auto_translated: true`.
  // BEWUSST nur als Text-Overlay über `/product/review` und KEIN Quellwechsel: timeline/data hat
  // kein verified_buyer, keine Vor-/Nachnamen-Trennung und nur Bild-Dateinamen statt URLs →
  // Badge, Foto-Filter, Galerie und Schema bleiben so unverändert. Join über product_review_id.
  // DE/AT/CH (de) und alle anderen Sprachen: kein Extra-Call, Verhalten wie bisher.
  const TIMELINE_BASE = 'https://api.reviews.io/timeline/data';
  const TRANSLATE_LOCALES = ['es', 'fr', 'it', 'nl'];
  const TRANSLATE = TRANSLATE_LOCALES.includes(LOCALE);
  const TIMELINE_PER_PAGE = 100;
  // ST-PERF 2026-09-30 (Wrike 4575801422, Runde 2): Seite 1 blockiert den Initial-Render →
  // hart deckeln (gemessen ungecacht 0,6–1,1 s). Kommt sie nicht rechtzeitig, rendert das
  // Widget deutsch wie bisher und die Übersetzung kommt über den Hintergrund-Load nach.
  // BEWUSST 100 und nicht kleiner: die ersten sichtbaren Karten sind Foto-Reviews, die in
  // der date_desc-Timeline weit hinten stehen (F10: Position 84–120); timeline/data kann
  // nicht nach Fotos filtern.
  const TIMELINE_FIRST_TIMEOUT_MS = 1500;
  // ST-FIX 2026-09-30 (Wrike 4576137950): Late-Switch. Die ersten sichtbaren Karten (Foto-Reviews)
  // stehen in der Timeline auf Seite 2–3 (F75: 23 von 25 Foto-Reviews nicht auf Seite 1), die kamen
  // vorher erst nach Seite 1 (bei Timeout sogar abgebrochen und neu geladen) in 3er-Chunks mit
  // priority 'low' → bei langsamem Reviews.io 19–32 s deutsch. Jetzt: ALLE Seiten sofort ab
  // Seitenstart (Anzahl geschätzt aus dem Metafeld reviews.rating_count), max. 4 gleichzeitig,
  // normale Priorität, nichts wird abgebrochen. Der Deckel oben gilt nur fürs Warten.
  const TIMELINE_CONCURRENCY = 4;
  // ST-PERF 2026-09-30 (Wrike 4576137950): Double-Loading. /product/review akzeptiert per_page bis
  // 1000 (größtes Produkt F37s: 751 Reviews) → Seiten 2..N werden durch EINEN Hintergrund-Request
  // ersetzt (sRow 3 → 1, F37s 7 → 1). Seite 1 bleibt bei 100, damit der Initial-Render schnell
  // bleibt (gemessen ungecacht: 100 = 0,4–0,6 s, 1000 = 1,1–1,7 s). Die ersten 100 kommen darin
  // doppelt (~16 KB gzip) und werden per dedupeReviews verworfen.
  const REST_PER_PAGE = 1000;

  // ST-FIX 2026-09-30 (Wrike 4575937229): Der Liquid-`t`-Filter liefert die Strings bereits
  // HTML-escaped (' → &#39;), escapeHtml() escaped beim Rendern ein zweites Mal → sichtbares
  // "&#39;" in FR/IT/NL. Deshalb hier einmal zurück in Klartext dekodieren; escapeHtml()
  // bleibt die einzige Escape-Stufe. <textarea> dekodiert nur Entities, parst kein Markup.
  const decodeEntities = (str) => {
    if (typeof str !== 'string' || str.indexOf('&') === -1) return str;
    const el = document.createElement('textarea');
    el.innerHTML = str;
    return el.value;
  };

  // Locale-aware translation lookup with {{ var }} interpolation
  const t = (key, vars = {}) => {
    let str = decodeEntities(I18N[key]) || key;
    for (const [k, v] of Object.entries(vars)) {
      str = str.replace(new RegExp(`\\{\\{\\s*${k}\\s*\\}\\}`, 'g'), v);
    }
    return str;
  };

  // Icon-Set (SVG, currentColor-based, scaled via wrapper-font-size)
  const ICON = {
    chevron: '<svg class="st-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><polyline points="6 9 12 15 18 9"/></svg>',
    search: '<svg class="st-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><circle cx="11" cy="11" r="7"/><line x1="21" y1="21" x2="16.5" y2="16.5"/></svg>',
    camera: '<svg class="st-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.7" stroke-linecap="round" stroke-linejoin="round"><path d="M23 19a2 2 0 0 1-2 2H3a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h4l2-3h6l2 3h4a2 2 0 0 1 2 2z"/><circle cx="12" cy="13" r="4"/></svg>',
    checkCircle: '<svg class="st-icon" viewBox="0 0 16 16" fill="currentColor"><path d="M8 0a8 8 0 1 0 0 16A8 8 0 0 0 8 0zm3.7 6.3l-4 4a.8.8 0 0 1-1.1 0l-2-2a.8.8 0 1 1 1.1-1.1L7.1 8.6l3.5-3.4a.8.8 0 1 1 1.1 1.1z"/></svg>',
    close: '<svg class="st-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round"><line x1="6" y1="6" x2="18" y2="18"/><line x1="18" y1="6" x2="6" y2="18"/></svg>',
    play: '<svg class="st-icon" viewBox="0 0 24 24" fill="currentColor"><polygon points="6 4 20 12 6 20"/></svg>'
  };

  const STATE = {
    reviews: [],
    stats: null,
    visibleCount: INITIAL_VISIBLE,
    // ST-FEAT 2026-08-12 (Wrike 4528640846): Default = Foto-Reviews zuerst.
    // BEWUSST eine Sortierung, KEIN harter Filter: 17 von 86 Produkten mit Reviews
    // haben gar kein Foto und 22 nur 1–4 — ein Default-Filter hätte dort eine
    // (fast) leere Liste erzeugt, obwohl teils 90–450 Bewertungen existieren.
    // Als Sortierung ist es bei foto-losen Produkten ein No-Op (Reihenfolge =
    // wie vorher nach Datum) und die Bewertungs-Anzahl bleibt vollständig sichtbar.
    sort: 'photos',
    filterRating: null,    // null = all | 1..5
    filterPhotos: false,
    filterVerified: false,
    search: '',
    root: null,
    // ST-FEAT 2026-09-30 (Wrike 4575801422): product_review_id → { title, text } aus timeline/data
    translations: new Map(),
    // product_review_ids, bei denen der User „Original anzeigen“ geklickt hat (überlebt Re-Render)
    showOriginal: new Set(),
  };

  // ---------- Utils ----------
  const $ = (sel, ctx = document) => ctx.querySelector(sel);
  const create = (tag, attrs = {}, html = '') => {
    const el = document.createElement(tag);
    for (const [k, v] of Object.entries(attrs)) {
      if (k === 'class') el.className = v;
      else if (k.startsWith('on')) el.addEventListener(k.slice(2), v);
      else el.setAttribute(k, v);
    }
    if (html) el.innerHTML = html;
    return el;
  };
  const escapeHtml = (str) => String(str ?? '').replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]));
  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    const d = new Date(dateStr);
    if (isNaN(d)) return '';
    try {
      return new Intl.DateTimeFormat(LOCALE, { day: '2-digit', month: '2-digit', year: 'numeric' }).format(d);
    } catch {
      return d.toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit', year: 'numeric' });
    }
  };
  // Fractional fill via .st-stars (see reviews-io.css). Any fraction renders as
  // a half star, never a full one — a 4.83 must not look like a genuine 5.0.
  // Decorative: the score and count next to it already carry the value.
  const stars = (rating) => {
    const value = Number(rating) || 0;
    const whole = Math.floor(value);
    const decimal = value - whole > 0 ? 0.5 : 0;
    return `<span class="st-stars" style="--st-stars-whole:${whole};--st-stars-decimal:${decimal}" aria-hidden="true"></span>`;
  };
  const getSku = () =>
    window.ShopifyAnalytics?.meta?.product?.variants?.[0]?.sku ||
    $('meta[itemprop="sku"]')?.getAttribute('content') ||
    null;

  // ST 2026-10-12 (Wrike 4503597748): no client-side Product schema any more. aggregateRating is rendered
  // server-side on the ProductGroup in sections/main-product.liquid (decision-log D-50).

  // ---------- API ----------
  async function fetchPage(sku, page = 1, perPage = PER_PAGE) {
    // minRating=1 erzwingt komplette Rating-Range (API filtert sonst per default).
    // lang wird von /product/review ignoriert (immer Original) — Übersetzungen kommen aus timeline/data (s. TRANSLATE).
    const url = `${API_BASE}?store=${encodeURIComponent(STORE)}&sku=${encodeURIComponent(sku)}&per_page=${perPage}&page=${page}&lang=${encodeURIComponent(LOCALE)}&minRating=1`;
    const res = await fetch(url, { credentials: 'omit' });
    if (!res.ok) throw new Error(`API ${res.status}`);
    return res.json();
  }

  // Reviews.io liefert reviews als Laravel-Pagination-Objekt: {current_page, data: [...], last_page, ...}
  const extractReviews = (resp) => Array.isArray(resp?.reviews) ? resp.reviews : (resp?.reviews?.data || []);

  // ST-FEAT 2026-08-12 (Wrike 4528640846): Nur Reviews MIT Foto, server-seitig gefiltert.
  // Fehler sind hier absichtlich unkritisch (→ []): die Foto-Priorisierung ist ein
  // Nice-to-have, sie darf den Initial-Render der Liste nie blockieren oder killen.
  async function fetchPhotoReviews(sku) {
    try {
      const url = `${API_BASE}?store=${encodeURIComponent(STORE)}&sku=${encodeURIComponent(sku)}&per_page=${PHOTO_PER_PAGE}&page=1&lang=${encodeURIComponent(LOCALE)}&minRating=1&photos=true`;
      const res = await fetch(url, { credentials: 'omit' });
      if (!res.ok) return [];
      return extractReviews(await res.json());
    } catch (e) {
      return [];
    }
  }

  // ST-FEAT 2026-08-12 (Wrike 4528640846): Dedupe über `product_review_id`.
  // PFLICHT, weil der Foto-Call Reviews vorzieht, die später über die normale
  // Paginierung ohnehin nochmal ankommen. Ohne Dedupe würden sie doppelt in der
  // Liste stehen UND Breakdown-Bars + ld+json-Schema verfälschen.
  // Reviews ohne id (Altimporte) werden durchgelassen — lieber ein potenzielles
  // Duplikat als ein verschluckter Review.
  function dedupeReviews(list) {
    const seen = new Set();
    const out = [];
    for (const r of list) {
      const id = r && r.product_review_id;
      if (id == null) { out.push(r); continue; }
      if (seen.has(id)) continue;
      seen.add(id);
      out.push(r);
    }
    return out;
  }

  // ---------- Übersetzungen (ST-FEAT 2026-09-30, Wrike 4575801422) ----------
  // Fehler sind unkritisch (→ null): ohne Übersetzung bleibt die Review deutsch, wie bisher.
  // ST-FIX 2026-09-30 (Wrike 4576137950): kein AbortController und kein priority 'low' mehr —
  // die Seiten entscheiden jetzt, ob die sichtbaren Karten übersetzt sind (s. TIMELINE_CONCURRENCY).
  async function fetchTranslationPage(sku, page) {
    try {
      const url = `${TIMELINE_BASE}?type=product_review&store=${encodeURIComponent(STORE)}&sku=${encodeURIComponent(sku)}&lang=${encodeURIComponent(LOCALE)}&sort=date_desc&per_page=${TIMELINE_PER_PAGE}&page=${page}`;
      const res = await fetch(url, { credentials: 'omit' });
      if (!res.ok) return null;
      return await res.json();
    } catch (e) {
      return null;
    }
  }

  // timeline-Items: { _id: 'product_review-58081857', _source: { comments, review_title, auto_translated, … } }
  function addTranslations(resp) {
    const list = Array.isArray(resp?.timeline) ? resp.timeline : [];
    list.forEach((item) => {
      const src = item?._source || {};
      if (!src.auto_translated || !src.comments) return;
      const id = Number(String(src._id || item._id || '').replace('product_review-', ''));
      if (!id) return;
      STATE.translations.set(id, { title: src.review_title || '', text: src.comments });
    });
  }

  // Übersetzung in die Review-Objekte schreiben, Original in st_original sichern.
  // Idempotent (st_original gesetzt = schon übersetzt). Rückgabe: Set der geänderten Reviews.
  function applyTranslations(reviews) {
    const changed = new Set();
    if (!TRANSLATE || !STATE.translations.size) return changed;
    for (const r of reviews) {
      if (!r || r.st_original) continue;
      const tr = STATE.translations.get(Number(r.product_review_id));
      if (!tr) continue;
      r.st_original = { title: r.title || '', review: r.review || '' };
      r.title = tr.title;
      r.review = tr.text;
      changed.add(r);
    }
    return changed;
  }

  // Nach jeder angekommenen Seite: Übersetzungen auf die schon gerenderten Reviews anwenden.
  // Vor dem Initial-Render ist STATE.reviews leer → nur sammeln, fetchProgressive wendet sie an.
  // ST-PERF 2026-09-30 (Runde 2): Liste nur neu rendern, wenn eine gerade SICHTBARE Karte neu
  // übersetzt wurde (z. B. ältere Foto-Reviews) — sonst kein DOM-Rebuild, kein Layout-Shift.
  function refreshTranslations() {
    const changed = applyTranslations(STATE.reviews);
    if (!changed.size || !STATE.root || !STATE.root.querySelector('.st-list')) return;
    const visible = getFilteredSorted().slice(0, STATE.visibleCount);
    if (visible.some((r) => changed.has(r))) renderListOnly();
  }

  // ST-FIX 2026-09-30 (Wrike 4576137950): alle Timeline-Seiten ab Seitenstart, max.
  // TIMELINE_CONCURRENCY parallel. Seitenzahl nur als Schätzung aus dem Metafeld — es weicht in
  // beide Richtungen ab (F37s 1735 vs. 751 bei Reviews.io, sRow 348 vs. 376). Die erste Antwort
  // (stats.results_count) ist maßgeblich: überzählige Seiten fliegen aus der Queue, fehlende
  // kommen dazu. Fehlgeschlagene Seiten einmal nach 2 s wiederholen. Resolved, wenn alles da ist.
  function loadTranslations(sku) {
    return new Promise((resolve) => {
      const estimate = Math.max(1, Math.ceil((Number(CONFIG.reviewCount) || 0) / TIMELINE_PER_PAGE));
      let queue = [];
      for (let p = 1; p <= estimate; p++) queue.push(p);
      const queued = new Set(queue);
      let active = 0;
      const run = async (page) => {
        let resp = await fetchTranslationPage(sku, page);
        if (!resp) {
          await sleep(2000);
          resp = await fetchTranslationPage(sku, page);
        }
        if (!resp) return;
        const realLast = Math.ceil((Number(resp.stats?.results_count) || 0) / TIMELINE_PER_PAGE);
        if (realLast) {
          queue = queue.filter((p) => p <= realLast);
          for (let p = 2; p <= realLast; p++) if (!queued.has(p)) { queued.add(p); queue.push(p); }
        }
        addTranslations(resp);
        refreshTranslations();
      };
      const pump = () => {
        while (active < TIMELINE_CONCURRENCY && queue.length) {
          active++;
          run(queue.shift()).catch(() => {}).finally(() => {
            active--;
            pump();
            if (!active && !queue.length) resolve();
          });
        }
      };
      pump();
    });
  }

  // ST-FIX 2026-07-06 (B9): Page-1-Retry mit Backoff — vorher kostete EIN transienter
  // API-Fehler die komplette Section für die Session (Sofort-Hide ohne 2. Versuch).
  const sleep = (ms) => new Promise((resolve) => setTimeout(resolve, ms));
  async function fetchPageWithRetry(sku, page, retries = 2) {
    for (let attempt = 0; ; attempt++) {
      try {
        return await fetchPage(sku, page);
      } catch (e) {
        if (attempt >= retries) throw e;
        await sleep(attempt === 0 ? 1000 : 3000);
      }
    }
  }

  // Progressive Loading:
  // - Page 1 wartet auf Response (Initial-Render mit erste 100 Reviews + Stats)
  // - Pages 2-N laden im Hintergrund + triggern Re-Render on-arrival
  // → User sieht Widget sofort, Breakdown/Schema werden präziser über Zeit.
  async function fetchProgressive(sku, onUpdate) {
    // ST-FEAT 2026-08-12 (Wrike 4528640846): Foto-Call läuft PARALLEL zu Seite 1 —
    // nicht danach. Die Liste wird bei Folge-Seiten bewusst nicht neu gerendert
    // (siehe init()), also müssen die Foto-Reviews schon beim Initial-Render da
    // sein, sonst greift die Priorisierung erst nach einer User-Interaktion.
    // Promise.all statt await-Kette: kostet keine zusätzliche Wartezeit.
    // ST-FEAT 2026-09-30 (Wrike 4575801422): Übersetzungen ebenfalls parallel, damit der
    // Initial-Render direkt übersetzt ist statt kurz deutsch aufzublitzen.
    // ST-FIX 2026-09-30 (Wrike 4576137950): gewartet wird auf ALLE Timeline-Seiten (die sichtbaren
    // Foto-Reviews stehen weit hinten), höchstens TIMELINE_FIRST_TIMEOUT_MS. Danach rendert das
    // Widget deutsch; die Requests laufen weiter und refreshTranslations() tauscht die Texte.
    const translationsDone = TRANSLATE ? loadTranslations(sku) : null;
    const [first, photoBatch] = await Promise.all([
      fetchPageWithRetry(sku, 1),
      fetchPhotoReviews(sku),
      translationsDone ? Promise.race([translationsDone, sleep(TIMELINE_FIRST_TIMEOUT_MS)]) : null
    ]);
    const firstBatch = dedupeReviews(photoBatch.concat(extractReviews(first)));
    applyTranslations(firstBatch);
    const lastPage = first.reviews?.last_page || 1;
    onUpdate({
      stats: first.stats || { average: 0, count: firstBatch.length },
      reviews: firstBatch,
      isInitial: true,
      isFinal: lastPage <= 1
    });
    if (lastPage <= 1) return;

    // ST-FIX 2026-07-06 (B9): Folge-Seiten nacheinander statt alle gleichzeitig (Rate-Limit-Risiko).
    // ST-PERF 2026-09-30 (Wrike 4576137950): Rest mit REST_PER_PAGE statt Seiten 2..N à 100 —
    // heute für jedes Produkt EIN Request. Einzelfehler = Seite überspringen, wie bisher.
    const restLast = Math.max(1, Math.ceil((Number(first.reviews?.total) || 0) / REST_PER_PAGE));
    let accumulated = firstBatch.slice();
    for (let p = 1; p <= restLast; p++) {
      const data = extractReviews(await fetchPage(sku, p, REST_PER_PAGE).catch(() => null));
      // Dedupe: die ersten 100 und die vorgezogenen Foto-Reviews kommen hier ein zweites Mal.
      // Die schon gerenderten (ggf. übersetzten) Objekte bleiben erhalten.
      if (data.length) accumulated = dedupeReviews(accumulated.concat(data));
      applyTranslations(accumulated);
      onUpdate({
        stats: first.stats || { average: 0, count: accumulated.length },
        reviews: accumulated,
        isInitial: false,
        isFinal: p === restLast
      });
    }
  }

  // ---------- Computed Stats ----------
  // Hero-Stats aus allen 1-5★ Reviews (average, total, counts je Stufe).
  function computeBreakdown(reviews) {
    const counts = { 1: 0, 2: 0, 3: 0, 4: 0, 5: 0 };
    let sum = 0;
    let total = 0;
    reviews.forEach((r) => {
      const rt = Math.round(Number(r.rating) || 0);
      if (rt >= 1 && rt <= 5) {
        counts[rt]++;
        sum += Number(r.rating) || 0;
        total++;
      }
    });
    const average = total > 0 ? sum / total : 0;
    // Empfehlung% = Anteil 5★ Reviews (gleicher Wert wie 5★-Bar im Hero)
    const allTotal = reviews.length;
    const recommend = allTotal > 0 ? Math.round((counts[5] / allTotal) * 100) : 0;
    return { counts, total, allTotal, average, recommend };
  }

  // ST 2026-10-27 (Wrike 4592398622): Gesamtzahl und Durchschnitt (Hero, Kaufbox, aggregateRating) aus
  // Reviews.io `stats` (count/average über alle Bewertungen). Die Paginierung liefert vereinzelt Doubletten
  // und lässt dafür eine Bewertung aus (Bodenschutzmatten: 132 geladen, stats 133); fehlende Seiten werden
  // übersprungen. Fallback: gezählte Reviews (bd), wenn stats fehlt.
  function headline(bd) {
    const count = Number(STATE.stats?.count) || 0;
    const average = parseFloat(STATE.stats?.average) || 0;
    return count > 0 && average > 0 ? { count, average } : { count: bd.total, average: bd.average };
  }

  // Reviews.io: API liefert direkte S3-URLs (403-Forbidden). Echte Bilder werden via
  // media.reviews.co.uk Resize-Proxy serviert. Thumbnails klein, Lightbox via Original.
  const rawImageUrl = (img) => img?.image || img?.url || (typeof img === 'string' ? img : null);
  const videoUrl = (v) => v?.video || v?.url || (typeof v === 'string' ? v : null);

  const thumbUrl = (img, size = 220) => {
    const raw = rawImageUrl(img);
    if (!raw) return null;
    // Square crop für Thumbnails (CSS object-fit:cover trimt sauber)
    return `https://media.reviews.co.uk/resize/create?format=jpg&width=${size}&height=${size}&src=${encodeURIComponent(raw)}`;
  };
  // Lightbox: Original-Bild via Reviews.io CDN (CloudFront) → echtes Seitenverhältnis,
  // kein weißer Rand vom Resize-Proxy. S3-URL wird auf CloudFront umgeschrieben.
  const fullUrl = (img) => {
    const raw = rawImageUrl(img);
    if (!raw) return null;
    return raw.replace(
      /^https:\/\/s3[^/]+\.amazonaws\.com\/reviewscouk\//,
      'https://d1011j0lbv5k1u.cloudfront.net/'
    );
  };
  // Legacy compat
  const _imageUrl = thumbUrl;

  function collectPhotos(reviews, limit = 24) {
    const out = [];
    for (const r of reviews) {
      if (Array.isArray(r.images)) {
        for (const img of r.images) {
          const t = thumbUrl(img, 220);
          const f = fullUrl(img);
          if (t) out.push({ type: 'image', thumb: t, full: f });
          if (out.length >= limit + 8) return out;
        }
      }
      if (Array.isArray(r.videos)) {
        for (const v of r.videos) {
          const url = videoUrl(v);
          if (url) out.push({ type: 'video', thumb: url, full: url });
        }
      }
    }
    return out;
  }

  // ---------- Filtering / Sorting / Search ----------
  function getFilteredSorted() {
    let list = STATE.reviews.slice();
    if (STATE.filterRating) {
      list = list.filter((r) => Math.round(Number(r.rating) || 0) === STATE.filterRating);
    }
    if (STATE.filterPhotos) {
      list = list.filter((r) => Array.isArray(r.images) && r.images.length > 0);
    }
    if (STATE.filterVerified) {
      list = list.filter((r) => r.reviewer?.verified_buyer || r.verified_buyer);
    }
    if (STATE.search) {
      const q = STATE.search.toLowerCase();
      list = list.filter((r) =>
        (r.title || '').toLowerCase().includes(q) ||
        (r.review || '').toLowerCase().includes(q) ||
        // ST-FEAT 2026-09-30: auch im deutschen Original suchen (übersetzte Reviews)
        (r.st_original?.review || '').toLowerCase().includes(q) ||
        (r.reviewer?.first_name || '').toLowerCase().includes(q)
      );
    }

    const ts = (r) => new Date(r.date_created).getTime() || 0;
    const hasPhoto = (r) => (Array.isArray(r.images) && r.images.length > 0 ? 1 : 0);
    switch (STATE.sort) {
      // ST-FEAT 2026-08-12 (Wrike 4528640846): Default. Foto-Reviews oben, innerhalb
      // beider Gruppen neueste zuerst → foto-lose Produkte sehen aus wie 'newest'.
      case 'photos': list.sort((a, b) => hasPhoto(b) - hasPhoto(a) || ts(b) - ts(a)); break;
      case 'oldest': list.sort((a, b) => ts(a) - ts(b)); break;
      case 'best': list.sort((a, b) => Number(b.rating) - Number(a.rating) || ts(b) - ts(a)); break;
      case 'popular': list.sort((a, b) => (Number(b.votes) || 0) - (Number(a.votes) || 0) || ts(b) - ts(a)); break;
      case 'newest':
      default: list.sort((a, b) => ts(b) - ts(a)); break;
    }
    return list;
  }

  // ---------- Render: Hero ----------
  function renderHero() {
    // Stats aus allen Reviews — konsistent zur Card-Liste
    const bd = computeBreakdown(STATE.reviews);
    const head = headline(bd);
    const avg = head.average.toFixed(2);
    const count = head.count;

    return `
      <div class="st-reviews__hero">
        <div class="st-hero__score">
          <div class="st-hero__num">${avg}</div>
          <div class="st-hero__stars">${stars(avg)}</div>
          <div class="st-hero__count">${escapeHtml(t('reviews_count', { count }))}</div>
        </div>
        <div class="st-hero__bars">
          ${[5,4,3,2,1].map((s) => {
            const c = bd.counts[s] || 0;
            const pct = bd.total > 0 ? Math.round((c / bd.total) * 100) : 0;
            return `<div class="st-bar"><span class="st-bar__lbl">${s}★</span><div class="st-bar__track"><div class="st-bar__fill" style="--st-fill:${pct}%"></div></div><span class="st-bar__pct">${pct}%</span></div>`;
          }).join('')}
        </div>
        <div class="st-hero__meta">
          <span class="st-hero__recommend"><span class="st-check">✓</span>${escapeHtml(t('recommend', { percent: bd.recommend }))}</span>
          <button type="button" class="st-hero__info" data-st-info>${escapeHtml(t('info_link'))}</button>
        </div>
      </div>
    `;
  }

  // ---------- Render: Gallery ----------
  function renderGallery() {
    const photos = collectPhotos(STATE.reviews, 12);
    if (photos.length === 0) return '';
    const visible = photos.slice(0, 12);
    return `
      <div class="st-gallery">
        <div class="st-gallery__head">
          <h3 class="st-gallery__title">${escapeHtml(t('gallery_title'))}</h3>
          <span class="st-gallery__count">${escapeHtml(t('gallery_count', { count: photos.length }))}</span>
        </div>
        <div class="st-gallery__scroll" data-st-gallery>
          ${visible.map((p, i) => {
            const media = p.type === 'video'
              ? `<video src="${escapeHtml(p.thumb)}#t=0.1" preload="metadata" muted playsinline></video>`
              : `<img src="${escapeHtml(p.thumb)}" alt="" loading="lazy" decoding="async">`;
            const play = p.type === 'video' ? '<span class="st-thumb__play">' + ICON.play + '</span>' : '';
            return `<button class="st-thumb${p.type === 'video' ? ' st-thumb--video' : ''}" type="button" data-st-photo="${i}" aria-label="${escapeHtml(t(p.type === 'video' ? 'thumb_video' : 'thumb_photo', { index: i + 1 }))}">${media}${play}</button>`;
          }).join('')}
        </div>
      </div>
    `;
  }

  // ---------- Render: Toolbar (Sortieren-Popover + Suchen-Input) ----------
  // ST-FEAT 2026-08-12 (Wrike 4528640846): 'photos' zuerst = Default-Option oben im Popover.
  const SORT_KEYS = ['photos', 'newest', 'oldest', 'popular', 'best'];

  function renderToolbar() {
    const bd = computeBreakdown(STATE.reviews);
    return `
      <div class="st-toolbar">
        <button class="st-tb-trigger st-tb-trigger--active" data-st-toggle="sort" type="button">
          ${escapeHtml(t('sort'))} ${ICON.chevron}
        </button>
        <input class="st-search-input" type="search" placeholder="${escapeHtml(t('search_placeholder'))}" value="${escapeHtml(STATE.search)}" data-st-search>

        <div class="st-popover" data-st-pop="sort" hidden>
          <div class="st-pop__top"><button class="st-pop__clear" type="button" data-st-clear>${escapeHtml(t('clear_filter'))}</button></div>

          <div class="st-pop__section">
            <h6 class="st-pop__heading">${escapeHtml(t('sort_heading'))}</h6>
            <div class="st-pop__opts">
              ${SORT_KEYS.map((key) => `
                <button type="button" class="st-pop__opt${STATE.sort === key ? ' st-pop__opt--active' : ''}" data-st-sort="${key}"><span>${escapeHtml(t('sort_' + key))}</span></button>
              `).join('')}
            </div>
          </div>

          <div class="st-pop__section">
            <h6 class="st-pop__heading">${escapeHtml(t('breakdown_heading'))}</h6>
            <div class="st-pop__opts">
              ${[5,4,3,2,1].map((s) => {
                const c = bd.counts[s] || 0;
                return `<button type="button" class="st-pop__opt${STATE.filterRating === s ? ' st-pop__opt--active' : ''}" data-st-rating="${s}">
                  <span class="st-pop__opt-stars">${'★'.repeat(s)}</span>
                  <span class="st-pop__opt-count">${c}</span>
                </button>`;
              }).join('')}
            </div>
          </div>

          <div class="st-pop__section">
            <h6 class="st-pop__heading">${escapeHtml(t('filter_heading'))}</h6>
            <div class="st-pop__opts">
              <button type="button" class="st-pop__opt${STATE.filterPhotos ? ' st-pop__opt--active' : ''}" data-st-toggle-photos>
                <span class="st-pop__opt-label">${ICON.camera}${escapeHtml(t('filter_photos'))}</span>
                <span class="st-pop__opt-count">${STATE.reviews.filter(r => Array.isArray(r.images) && r.images.length).length}</span>
              </button>
              <button type="button" class="st-pop__opt${STATE.filterVerified ? ' st-pop__opt--active' : ''}" data-st-toggle-verified>
                <span class="st-pop__opt-label">${ICON.checkCircle}${escapeHtml(t('filter_verified'))}</span>
                <span class="st-pop__opt-count">${STATE.reviews.filter(r => r.reviewer?.verified_buyer || r.verified_buyer).length}</span>
              </button>
            </div>
          </div>
        </div>
      </div>
    `;
  }

  // ---------- Render: Cards ----------
  function renderCard(r) {
    const name = r.reviewer?.first_name
      ? `${r.reviewer.first_name}${r.reviewer.last_name ? ' ' + r.reviewer.last_name.charAt(0) + '.' : ''}`
      : t('anonymous');
    const verified = r.reviewer?.verified_buyer || r.verified_buyer;
    const date = formatDate(r.date_created);
    const rating = Math.round(Number(r.rating) || 0);
    const images = Array.isArray(r.images) ? r.images : [];
    // ST-FEAT 2026-09-30 (Wrike 4575801422): übersetzte Review → Label + Umschalter aufs Original
    const orig = r.st_original;
    const showOrig = !!orig && STATE.showOriginal.has(r.product_review_id);
    const src = showOrig ? orig : r;
    const title = src.title && src.title !== name ? src.title : '';
    const text = (src.review || '').trim();
    const translated = orig ? `
        <p class="st-card__translated">
          <span>${escapeHtml(t('auto_translated'))}</span>
          <span aria-hidden="true">·</span>
          <button type="button" class="st-card__original-toggle" data-st-original="${escapeHtml(r.product_review_id)}" aria-pressed="${showOrig}">${escapeHtml(t(showOrig ? 'show_translation' : 'show_original'))}</button>
        </p>` : '';

    return `
      <article class="st-card">
        <header class="st-card__head">
          <div class="st-card__who">
            <h4 class="st-card__name">${escapeHtml(name)}</h4>
            <div class="st-card__meta">
              <span class="st-card__stars" role="img" aria-label="${escapeHtml(t('rating_label', { rating }))}">${'★'.repeat(rating)}${'☆'.repeat(5 - rating)}</span>
              ${verified ? `<span class="st-verified"><svg viewBox="0 0 16 16" fill="currentColor"><path d="M8 0a8 8 0 1 0 0 16A8 8 0 0 0 8 0zm3.7 6.3l-4 4a.8.8 0 0 1-1.1 0l-2-2a.8.8 0 1 1 1.1-1.1L7.1 8.6l3.5-3.4a.8.8 0 1 1 1.1 1.1z"/></svg>${escapeHtml(t('verified_purchase'))}</span>` : ''}
            </div>
          </div>
          ${date ? `<span class="st-card__date">${date}</span>` : ''}
        </header>
        ${title ? `<h5 class="st-card__title">${escapeHtml(title)}</h5>` : ''}
        ${text ? `<p class="st-card__text">${escapeHtml(text)}</p>` : ''}
        ${translated}
        ${images.length > 0 ? `
          <div class="st-card__photos">
            ${images.map((img) => {
              const tu = thumbUrl(img, 200);
              const fu = fullUrl(img);
              if (!tu) return '';
              return `<button class="st-thumb" type="button" data-st-photo-url="${escapeHtml(fu)}" aria-label="${escapeHtml(t('enlarge_photo'))}"><img src="${escapeHtml(tu)}" alt="" loading="lazy" decoding="async"></button>`;
            }).join('')}
          </div>
        ` : ''}
      </article>
    `;
  }

  function renderList() {
    const filtered = getFilteredSorted();
    if (filtered.length === 0) {
      return `<div class="st-list"><div class="st-empty">${escapeHtml(t('no_results'))}</div></div>`;
    }
    const visible = filtered.slice(0, STATE.visibleCount);
    return `
      <div class="st-list">
        ${visible.map(renderCard).join('')}
      </div>
      ${visible.length < filtered.length ? `
        <div class="st-loadmore">
          <button class="st-btn" type="button" data-st-loadmore>${escapeHtml(t('load_more'))}<span class="st-btn__progress">${escapeHtml(t('load_more_progress', { shown: visible.length, total: filtered.length }))}</span></button>
        </div>
      ` : ''}
    `;
  }

  // ---------- Render All ----------
  function render() {
    if (!STATE.root) return;
    STATE.root.innerHTML = `
      <h2 class="st-reviews__title">${escapeHtml(t('title'))}</h2>
      ${renderHero()}
      ${renderGallery()}
      ${renderToolbar()}
      ${renderList()}
    `;
    attachEvents();
  }

  function renderListOnly() {
    if (!STATE.root) return;
    // Find existing list+loadmore and replace
    const existingList = STATE.root.querySelector('.st-list');
    const existingMore = STATE.root.querySelector('.st-loadmore');
    if (existingList) existingList.remove();
    if (existingMore) existingMore.remove();
    const wrap = document.createElement('div');
    wrap.innerHTML = renderList();
    while (wrap.firstChild) STATE.root.appendChild(wrap.firstChild);
    attachListEvents();
  }

  function renderToolbarOnly() {
    if (!STATE.root) return;
    const old = STATE.root.querySelector('.st-toolbar');
    if (!old) return;
    const wrap = document.createElement('div');
    wrap.innerHTML = renderToolbar();
    old.replaceWith(wrap.firstElementChild);
    attachToolbarEvents();
  }

  // ---------- Events ----------
  function attachEvents() {
    attachToolbarEvents();
    attachListEvents();
    attachGalleryEvents();
    const info = STATE.root?.querySelector('[data-st-info]');
    if (info) info.onclick = openInfoModal;
  }

  function attachToolbarEvents() {
    if (!STATE.root) return;
    const trigger = STATE.root.querySelector('[data-st-toggle="sort"]');
    const pop = STATE.root.querySelector('[data-st-pop="sort"]');
    if (trigger && pop) {
      trigger.onclick = (e) => {
        e.stopPropagation();
        pop.hidden = !pop.hidden;
      };
      // ST-FIX 2026-07-06: Outside-Click war ein anonymer document-Listener, der bei jedem
      // Toolbar-Re-Render (Sort/Filter/Progressive-Load) NEU registriert und nie entfernt
      // wurde -> Listener-Leak + Zombie-Closures auf detached DOM. Jetzt genau EIN Listener
      // (remove-before-add). Rollback = diesen Block durch plain addEventListener ersetzen.
      if (STATE.onDocClick) document.removeEventListener('click', STATE.onDocClick);
      STATE.onDocClick = (e) => {
        if (!pop.contains(e.target) && !trigger.contains(e.target)) pop.hidden = true;
      };
      document.addEventListener('click', STATE.onDocClick);
    }

    STATE.root.querySelectorAll('[data-st-sort]').forEach((b) => {
      b.onclick = () => {
        STATE.sort = b.dataset.stSort;
        STATE.visibleCount = INITIAL_VISIBLE;
        renderToolbarOnly();
        renderListOnly();
      };
    });
    STATE.root.querySelectorAll('[data-st-rating]').forEach((b) => {
      b.onclick = () => {
        const r = parseInt(b.dataset.stRating, 10);
        STATE.filterRating = STATE.filterRating === r ? null : r;
        STATE.visibleCount = INITIAL_VISIBLE;
        renderToolbarOnly();
        renderListOnly();
      };
    });
    const togglePhotos = STATE.root.querySelector('[data-st-toggle-photos]');
    if (togglePhotos) togglePhotos.onclick = () => {
      STATE.filterPhotos = !STATE.filterPhotos;
      STATE.visibleCount = INITIAL_VISIBLE;
      renderToolbarOnly();
      renderListOnly();
    };
    const toggleVerified = STATE.root.querySelector('[data-st-toggle-verified]');
    if (toggleVerified) toggleVerified.onclick = () => {
      STATE.filterVerified = !STATE.filterVerified;
      STATE.visibleCount = INITIAL_VISIBLE;
      renderToolbarOnly();
      renderListOnly();
    };
    const clear = STATE.root.querySelector('[data-st-clear]');
    if (clear) clear.onclick = () => {
      // ST-FEAT 2026-08-12 (Wrike 4528640846): "Zurücksetzen" stellt den DEFAULT her,
      // und der ist jetzt 'photos' — nicht mehr 'newest'.
      STATE.sort = 'photos';
      STATE.filterRating = null;
      STATE.filterPhotos = false;
      STATE.filterVerified = false;
      STATE.search = '';
      STATE.visibleCount = INITIAL_VISIBLE;
      render();
    };
    const search = STATE.root.querySelector('[data-st-search]');
    if (search) {
      let t;
      search.oninput = (e) => {
        clearTimeout(t);
        t = setTimeout(() => {
          STATE.search = e.target.value;
          STATE.visibleCount = INITIAL_VISIBLE;
          renderListOnly();
        }, 200);
      };
    }
  }

  function attachListEvents() {
    if (!STATE.root) return;
    const more = STATE.root.querySelector('[data-st-loadmore]');
    if (more) more.onclick = () => {
      STATE.visibleCount += BATCH_LOAD;
      renderListOnly();
    };
    // Pro Karte alle Fotos zu einer Liste bündeln → Swipe/Blättern bleibt innerhalb der Karte.
    STATE.root.querySelectorAll('.st-card__photos').forEach((group) => {
      const btns = Array.from(group.querySelectorAll('[data-st-photo-url]'));
      const items = btns.map((b) => ({ type: 'image', full: b.dataset.stPhotoUrl, thumb: b.dataset.stPhotoUrl }));
      btns.forEach((b, i) => { b.onclick = () => openLightbox(items, i); });
    });
    // ST-FEAT 2026-09-30 (Wrike 4575801422): „Original anzeigen“ ↔ „Übersetzung anzeigen“.
    // Nur die eine Karte neu rendern (Liste/Scroll-Position bleiben), Fokus zurück auf den Button.
    STATE.root.querySelectorAll('[data-st-original]').forEach((b) => {
      b.onclick = () => {
        const id = b.dataset.stOriginal;
        const r = STATE.reviews.find((x) => String(x.product_review_id) === id);
        const card = b.closest('.st-card');
        if (!r || !card) return;
        if (STATE.showOriginal.has(r.product_review_id)) STATE.showOriginal.delete(r.product_review_id);
        else STATE.showOriginal.add(r.product_review_id);
        const wrap = document.createElement('div');
        wrap.innerHTML = renderCard(r).trim();
        const next = wrap.firstElementChild;
        card.replaceWith(next);
        attachListEvents();
        next.querySelector('[data-st-original]')?.focus();
      };
    });
  }

  function attachGalleryEvents() {
    if (!STATE.root) return;
    const photos = collectPhotos(STATE.reviews, 12);
    STATE.root.querySelectorAll('.st-gallery [data-st-photo]').forEach((b) => {
      b.onclick = () => {
        const i = parseInt(b.dataset.stPhoto, 10);
        if (photos[i]) openLightbox(photos, i);
      };
    });
  }

  // ---------- Info-Modal ("Wie sammeln wir Bewertungen?") ----------
  function openInfoModal() {
    if (document.querySelector('.st-modal[data-st-modal="info"]')) return;
    const html = `
      <div class="st-modal" data-st-modal="info" role="dialog" aria-modal="true" aria-labelledby="st-info-title">
        <div class="st-modal__card">
          <button class="st-modal__close" type="button" aria-label="${escapeHtml(t('close'))}" data-st-modal-close>${ICON.close}</button>
          <h3 id="st-info-title" class="st-modal__title">${escapeHtml(t('info_modal_title'))}</h3>
          <div class="st-modal__body">
            <p>${escapeHtml(t('info_modal_p1'))}</p>
            <p>${escapeHtml(t('info_modal_p2'))}</p>
            <p>${escapeHtml(t('info_modal_p3'))}</p>
          </div>
        </div>
      </div>
    `;
    const wrap = document.createElement('div');
    wrap.innerHTML = html.trim();
    const modal = wrap.firstElementChild;
    document.body.appendChild(modal);
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    const close = () => {
      modal.remove();
      document.body.style.overflow = prevOverflow;
      document.removeEventListener('keydown', onKey);
    };
    const onKey = (e) => { if (e.key === 'Escape') close(); };
    modal.querySelector('[data-st-modal-close]').onclick = close;
    modal.onclick = (e) => { if (e.target === modal) close(); };
    document.addEventListener('keydown', onKey);
  }

  // ---------- Lightbox (swipeable gallery viewer) ----------
  // ST-FIX 2026-08-10 (Wrike 4528640845): Lightbox war ein Single-Image-Viewer —
  // User mussten jedes Foto einzeln schließen/öffnen. Jetzt navigierbarer Gallery-Viewer:
  // Prev/Next-Pfeile (Desktop), Touch-Swipe (Mobile), Pfeiltasten + Zähler.
  // items = [{ full, thumb, type }]; startIndex = geklicktes Foto.
  // Backward-compat: openLightbox('url', 'video') funktioniert weiterhin (Single-Item) —
  // der Typ kam dort als ZWEITES Argument, nicht als drittes.
  function openLightbox(items, startIndex = 0, legacyType) {
    // Legacy-Aufruf mit String-URL → in Item-Liste normalisieren
    if (typeof items === 'string') {
      const type = (typeof startIndex === 'string' ? startIndex : legacyType) || 'image';
      items = [{ full: items, thumb: items, type }];
      startIndex = 0;
    }
    if (!Array.isArray(items) || items.length === 0) return;
    // Nur EINE Lightbox gleichzeitig (analog openInfoModal). Ohne Guard stapeln
    // sich zwei Instanzen und die zweite stellt beim Schließen `overflow:hidden`
    // wieder her → Seite bis zum Reload nicht mehr scrollbar.
    if (document.querySelector('.st-lightbox')) return;

    let idx = Math.max(0, Math.min(startIndex | 0, items.length - 1));
    const multi = items.length > 1;
    const prevOverflow = document.body.style.overflow;
    const prevFocus = document.activeElement;

    const box = create('div', {
      class: 'st-lightbox' + (multi ? ' st-lightbox--multi' : ''),
      role: 'dialog',
      'aria-modal': 'true',
      'aria-label': t('gallery_title'),
      tabindex: '-1'
    });
    box.innerHTML = `
      <button class="st-lightbox__close" type="button" aria-label="${escapeHtml(t('close'))}">${ICON.close}</button>
      ${multi ? `<button class="st-lightbox__nav st-lightbox__nav--prev" type="button" aria-label="${escapeHtml(t('prev_photo'))}">${ICON.chevron}</button>` : ''}
      <div class="st-lightbox__stage" data-st-stage></div>
      ${multi ? `<button class="st-lightbox__nav st-lightbox__nav--next" type="button" aria-label="${escapeHtml(t('next_photo'))}">${ICON.chevron}</button>` : ''}
      ${multi ? `<div class="st-lightbox__counter" data-st-counter aria-live="polite"></div>` : ''}
    `;
    const stage = box.querySelector('[data-st-stage]');
    const counter = box.querySelector('[data-st-counter]');

    // Laufendes Video vor dem Austausch stoppen — sonst lädt es im Hintergrund
    // weiter (Range-Requests) und hält beim Schließen einen offenen Buffer.
    const stopMedia = () => {
      const v = stage.querySelector('video');
      if (!v) return;
      try { v.pause(); v.removeAttribute('src'); v.load(); } catch {}
    };
    // Nachbarbilder vorladen: `full` ist das ungeresizte Original, sonst startet
    // der Download erst beim Blättern und die Stage bleibt kurz leer.
    const preloadNeighbours = () => {
      if (!multi) return;
      [idx + 1, idx - 1].forEach((n) => {
        const it = items[(n + items.length) % items.length];
        if (it && it.type !== 'video' && (it.full || it.thumb)) {
          const img = new Image();
          img.decoding = 'async';
          img.src = it.full || it.thumb;
        }
      });
    };
    const renderMedia = () => {
      const it = items[idx];
      const url = it.full || it.thumb;
      stopMedia();
      stage.innerHTML = it.type === 'video'
        ? `<video src="${escapeHtml(url)}" controls autoplay playsinline></video>`
        : `<img src="${escapeHtml(url)}" alt="" decoding="async">`;
      // Bei Video die Touch-Sperre aufheben, sonst ist der native Scrubber tot.
      // Blättern geht dort über die Pfeile.
      box.classList.toggle('has-video', it.type === 'video');
      if (counter) counter.textContent = `${idx + 1} / ${items.length}`;
      preloadNeighbours();
    };
    const go = (delta) => {
      if (!multi) return;
      idx = (idx + delta + items.length) % items.length; // Wraparound
      // Richtung für die Einblend-Animation: neues Bild kommt von der Seite,
      // aus der geblättert wird. Ohne das wirkt jeder Wechsel bewegungslos.
      stage.style.setProperty('--st-lb-from', delta > 0 ? '28px' : '-28px');
      renderMedia();
    };

    // ST-FIX 2026-07-06 (B9): keydown-Listener wird bei jedem Close-Pfad entfernt (kein Leak).
    const onKey = (e) => {
      if (e.key === 'Escape') { closeBox(); return; }
      if (e.key === 'ArrowLeft') { go(-1); return; }
      if (e.key === 'ArrowRight') { go(1); return; }
      // Fokusfalle: Tab darf nicht hinter das Overlay in die Seite laufen.
      if (e.key === 'Tab') {
        const focusable = box.querySelectorAll('button');
        if (!focusable.length) { e.preventDefault(); return; }
        const first = focusable[0];
        const last = focusable[focusable.length - 1];
        const on = document.activeElement;
        if (e.shiftKey && (on === first || on === box)) { e.preventDefault(); last.focus(); }
        else if (!e.shiftKey && on === last) { e.preventDefault(); first.focus(); }
        else if (!box.contains(on)) { e.preventDefault(); first.focus(); }
      }
    };
    const closeBox = () => {
      stopMedia();
      box.remove();
      document.body.style.overflow = prevOverflow;
      document.removeEventListener('keydown', onKey);
      // Fokus zurück auf das Thumbnail, von dem aus geöffnet wurde
      if (prevFocus && typeof prevFocus.focus === 'function') prevFocus.focus();
    };

    box.querySelector('.st-lightbox__close').onclick = (e) => { e.stopPropagation(); closeBox(); };
    const prevBtn = box.querySelector('.st-lightbox__nav--prev');
    const nextBtn = box.querySelector('.st-lightbox__nav--next');
    if (prevBtn) prevBtn.onclick = (e) => { e.stopPropagation(); go(-1); };
    if (nextBtn) nextBtn.onclick = (e) => { e.stopPropagation(); go(1); };

    // Backdrop-Klick schließt; Klick auf Bild/Video/Controls nicht.
    // suppressClick: nach einer Ziehgeste darf der folgende Klick NICHT schließen.
    let suppressClick = false;
    box.onclick = (e) => {
      if (suppressClick) { suppressClick = false; return; }
      if (e.target === box) closeBox();
    };
    stage.onclick = (e) => e.stopPropagation();

    // Ziehen zum Blättern — Pointer Events decken Maus (Desktop), Touch (Mobile)
    // und Stift mit EINEM Codepfad ab. Das Bild folgt dem Finger/Cursor und
    // schnappt zurück, wenn die Schwelle nicht erreicht wird.
    //
    // Handler hängen bewusst am GANZEN Viewer (box), nicht nur am Bild: bei
    // Hochformat-Fotos ist links/rechts breite Backdrop-Fläche, und genau dort
    // wischt man intuitiv. Am Bild allein blieb die Geste dort wirkungslos.
    if (multi) {
      const THRESHOLD = 40;      // px, ab hier wird geblättert
      let startX = 0, startY = 0, dx = 0, active = false, decided = false, horizontal = false;
      let snapTimer = null, rafId = 0, activeId = null;

      // Style-Write auf einen pro Frame bündeln — pointermove feuert auf
      // 120-Hz-Geräten deutlich öfter als der Compositor zeichnet.
      const scheduleShift = () => {
        if (rafId) return;
        rafId = requestAnimationFrame(() => {
          rafId = 0;
          stage.style.transform = dx && active ? `translateX(${dx}px)` : '';
        });
      };
      const setShiftNow = (px) => {
        if (rafId) { cancelAnimationFrame(rafId); rafId = 0; }
        stage.style.transform = px ? `translateX(${px}px)` : '';
      };
      const endDrag = (commit) => {
        if (!active) return;
        active = false;
        activeId = null;
        box.classList.remove('is-dragging');
        if (horizontal && Math.abs(dx) > 6) suppressClick = true;

        if (commit && horizontal && Math.abs(dx) > THRESHOLD) {
          // Blättern: KEIN Zurückfedern. Sonst federt das alte Bild sichtbar
          // zurück und der Wechsel selbst ist bewegungslos — das las sich für
          // den Nutzer wie „Swipe hat nicht funktioniert".
          stage.style.transition = '';
          setShiftNow(0);
          go(dx < 0 ? 1 : -1);
        } else {
          // Schwelle nicht erreicht → zurückfedern (hier ist es gewollt)
          stage.style.transition = 'transform 0.18s ease-out';
          setShiftNow(0);
          snapTimer = setTimeout(() => { stage.style.transition = ''; snapTimer = null; }, 200);
        }
      };

      box.addEventListener('pointerdown', (e) => {
        // Video-Controls (Scrubben) und die Buttons nicht kapern
        if (e.target.tagName === 'VIDEO') return;
        if (e.target.closest('.st-lightbox__nav, .st-lightbox__close')) return;
        if (e.pointerType === 'mouse' && e.button !== 0) return;
        // Zweiter Finger (z. B. Pinch-Zoom) darf den laufenden Drag nicht kapern.
        // Bewusst über isPrimary statt über `active`: bliebe `active` je einmal
        // hängen (Geste ohne pointerup/-cancel), wäre Wischen dauerhaft tot.
        if (!e.isPrimary) return;
        // Flag der VORIGEN Geste hier zurücksetzen. Auf Touch unterdrückt der
        // Browser nach einem Swipe den Klick komplett — box.onclick als einziger
        // Reset ließ das Flag hängen und schluckte danach den echten Tap.
        suppressClick = false;
        // Laufende Zurückfeder-Animation abbrechen — sonst laufen die Writes der
        // neuen Geste noch 180 ms durch die alte Transition (Bild hängt am Finger).
        if (snapTimer) { clearTimeout(snapTimer); snapTimer = null; }
        stage.style.transition = '';
        active = true; decided = false; horizontal = false; dx = 0;
        activeId = e.pointerId;
        startX = e.clientX; startY = e.clientY;
        box.classList.add('is-dragging');
      });

      box.addEventListener('pointermove', (e) => {
        if (!active || e.pointerId !== activeId) return;
        dx = e.clientX - startX;
        const dy = e.clientY - startY;
        // Richtung einmalig festlegen: vertikal → Geste dem Browser überlassen
        if (!decided && (Math.abs(dx) > 6 || Math.abs(dy) > 6)) {
          decided = true;
          horizontal = Math.abs(dx) > Math.abs(dy);
          if (!horizontal) { endDrag(false); return; }
          // Ab hier gehört die Geste uns — Pointer festhalten.
          // NUR für Maus/Stift: Touch-Browser (verifiziert iOS Safari) setzen den
          // Capture bereits IMPLIZIT auf das Ziel (<img>). Ein eigener Aufruf
          // verschiebt ihn und feuert dabei lostpointercapture am <img> — das
          // bubbelt zum Viewer und beendete die Geste nach wenigen Pixeln.
          if (e.pointerType !== 'touch') {
            try { box.setPointerCapture(e.pointerId); } catch {}
          }
        }
        if (horizontal) {
          e.preventDefault();
          scheduleShift();
        }
      });

      box.addEventListener('pointerup', (e) => { if (e.pointerId === activeId) endDrag(true); });
      box.addEventListener('pointercancel', (e) => { if (e.pointerId === activeId) endDrag(false); });
      // Fallback: geht der Pointer-Capture verloren (Maus außerhalb des Fensters
      // losgelassen), käme sonst nie ein pointerup — Drag bliebe hängen.
      // `e.target === box` ist Pflicht: das Event bubbelt auch, wenn ein
      // IMPLIZITER Capture am <img> endet — ohne die Prüfung beendet genau das
      // die Touch-Geste sofort (iOS Safari, verifiziert per Event-Log).
      box.addEventListener('lostpointercapture', (e) => {
        if (e.target === box && e.pointerId === activeId) endDrag(false);
      });
      // Bilder sind nativ draggable — würde die Geste abbrechen
      box.addEventListener('dragstart', (e) => e.preventDefault());
    }

    document.body.style.overflow = 'hidden';
    renderMedia();
    document.body.appendChild(box);
    // Fokus ins Overlay holen, sonst tabbt man hinter dem Dialog durch die Seite
    box.focus();
    document.addEventListener('keydown', onKey);
  }

  // ---------- Boot ----------
  function findMountPoint() {
    // Eigener Mount-Point aus sections/reviews-io.liquid (unabhängig von Reviews.io App-Embed)
    const mounts = document.querySelectorAll('.st-reviews-mount');
    if (mounts.length > 1) {
      // Section ist 3× im Template referenziert → nur ersten Mount-Point nutzen, Rest entfernen
      mounts.forEach((m, i) => { if (i > 0) m.remove(); });
    }
    return mounts[0] || null;
  }

  async function init() {
    const sku = getSku();
    if (!sku) return;

    const mount = findMountPoint();
    if (!mount) return;

    // Unsere Root direkt in den Mount-Point (eigener Container, frei platzierbar)
    let root = $('#st-reviews-root');
    if (!root) {
      root = create('div', { id: 'st-reviews-root', class: 'st-reviews is-loading' });
      mount.appendChild(root);
    }
    STATE.root = root;
    root.innerHTML = '<div class="st-skeleton"></div>';

    // Versteckt die ganze Section (inkl. Title/Wrapper) wenn keine Reviews darstellbar
    const hideSection = () => {
      const section = mount.closest('.shopify-section') || mount.closest('section') || root.parentElement;
      if (section) section.style.display = 'none';
    };

    try {
      await fetchProgressive(sku, ({ stats, reviews, isInitial, isFinal }) => {
        STATE.stats = stats;
        STATE.reviews = reviews;
        // Kein Produkt-Review vorhanden → komplette Section ausblenden
        if (isInitial && (!stats || !stats.count || stats.count === 0)) {
          hideSection();
          return;
        }
        if (isInitial) {
          // Erste Seite → komplettes Render, User sieht sofort etwas
          root.classList.remove('is-loading');
          render();
        } else {
          // Folge-Seiten → nur Hero + Toolbar updaten (Breakdown-Bars + Counts)
          // Liste bleibt unangetastet (kein UX-Flash, User-Position erhalten)
          updateHeroAndToolbar();
        }
        if (isFinal) {
          // Alle gefetched: keine darstellbare Bewertung → Section weg
          if (computeBreakdown(reviews || []).total === 0) {
            hideSection();
            return;
          }
          syncRatingDisplays(reviews);
        }
      });
    } catch (e) {
      console.warn('[st-reviews] fetch failed', e);
      // Bei Fetch-Error: Section ausblenden statt error message zeigen (saubere UX;
      // bewusste Entscheidung, bestätigt 2026-07-06 — greift jetzt erst nach 3 Versuchen).
      // ST-FIX 2026-07-06 (B9): Im Theme-Editor stattdessen load_error anzeigen,
      // damit Merchants sehen WARUM die Section leer ist (statt Bug zu vermuten).
      if (window.Shopify && window.Shopify.designMode) {
        root.classList.remove('is-loading');
        root.innerHTML = `<p class="st-reviews__editor-error" style="padding:16px 0;opacity:.7;">${escapeHtml(t('load_error'))}</p>`;
        return;
      }
      hideSection();
    }
  }

  // ---------- Buybox + schema sync ----------
  // ST 2026-10-27 (Wrike 4592398622, Ali): the buybox stars (snippets/product-rating.liquid) and aggregateRating
  // (sections/main-product.liquid) render the Reviews.io app metafields, which drift from what this section shows
  // (sPad1000: 364/4.8 vs. 361/4.82). Once all reviews are loaded, both take the hero's count and average
  // (headline(): Reviews.io stats over all 1-5★ reviews). Until then, and when the API fails or the section hides, the metafield values
  // stay. Only elements of this product (data-st-rating-sync = product id), never cards/quick view of others.
  // Average with 1 decimal like before (Liquid round: 1). Nothing is added where no stars were rendered.
  const PRODUCT_ID = String(CONFIG.productId || window.ShopifyAnalytics?.meta?.product?.id || '');
  function syncRatingDisplays(reviews) {
    if (!PRODUCT_ID) return;
    const bd = computeBreakdown(reviews || []);
    if (!bd.total) return;
    const head = headline(bd);
    const count = head.count;
    // Stars from the hero value (2 decimals, like stars() above), the number rounded to 1 decimal.
    const avg2 = Number(head.average.toFixed(2));
    const avg1 = (Math.round(avg2 * 10) / 10).toFixed(1);
    const whole = Math.floor(avg2);
    const decimal = avg2 - whole > 0 ? 0.5 : 0;
    document.querySelectorAll(`[data-st-rating-sync="${PRODUCT_ID}"]`).forEach((el) => {
      const star = el.querySelector('.rating-star');
      if (star) {
        star.style.setProperty('--rating', whole);
        star.style.setProperty('--rating-decimal', decimal);
      }
      const value = el.querySelector('.rating-text [aria-hidden="true"]');
      if (value) value.textContent = avg1;
      const img = el.querySelector('.rating[role="img"]');
      const infoTpl = el.getAttribute('data-st-info-tpl');
      if (img && infoTpl) img.setAttribute('aria-label', infoTpl.replace('%ST_R%', avg1));
      const countTpl = count === 1 ? el.getAttribute('data-st-count-one') : el.getAttribute('data-st-count-other');
      const countText = el.querySelector('.rating-count [aria-hidden="true"]');
      if (countText && countTpl) countText.textContent = countTpl.replace('987654321', String(count));
      const srCount = el.querySelector('.rating-count .sr-only');
      if (srCount) srCount.textContent = srCount.textContent.replace(/\d+/, String(count));
    });
    const ld = document.querySelector(`script[data-st-ld-product="${PRODUCT_ID}"]`);
    if (!ld) return;
    try {
      const data = JSON.parse(ld.textContent);
      if (!data || !data.aggregateRating) return;
      data.aggregateRating.ratingValue = Number(avg1);
      data.aggregateRating.reviewCount = count;
      ld.textContent = JSON.stringify(data);
    } catch {}
  }

  // Updates Hero + Toolbar in-place ohne List zu re-rendern (Performance-Friendly)
  function updateHeroAndToolbar() {
    if (!STATE.root) return;
    const oldHero = STATE.root.querySelector('.st-reviews__hero');
    if (oldHero) {
      const wrap = document.createElement('div');
      wrap.innerHTML = renderHero();
      oldHero.replaceWith(wrap.firstElementChild);
    }
    const oldGallery = STATE.root.querySelector('.st-gallery');
    const newGalleryHtml = renderGallery();
    if (newGalleryHtml) {
      const wrap = document.createElement('div');
      wrap.innerHTML = newGalleryHtml;
      if (oldGallery) {
        oldGallery.replaceWith(wrap.firstElementChild);
      } else {
        // Galerie war leer (keine Photos in Page 1), jetzt einfügen
        const toolbar = STATE.root.querySelector('.st-toolbar');
        if (toolbar) STATE.root.insertBefore(wrap.firstElementChild, toolbar);
      }
    }
    renderToolbarOnly();
    attachEvents();
  }

  function waitAndInit() {
    if (findMountPoint()) {
      init();
      return;
    }
    let tries = 0;
    const id = setInterval(() => {
      if (findMountPoint() || ++tries > 50) {
        clearInterval(id);
        if (findMountPoint()) init();
      }
    }, 300);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', waitAndInit);
  } else {
    waitAndInit();
  }

  // ST-FIX 2026-07-06 (B9): Editor-Lifecycle — Section-Re-Render im Theme-Editor
  // ersetzte den Mount-Point und killte das Widget bis zum Full-Reload.
  // shopify:section:load feuert nur im Editor; Re-Init ist idempotent
  // (findMountPoint dedupliziert, #st-reviews-root wird neu erzeugt, STATE.onDocClick räumt auf).
  document.addEventListener('shopify:section:load', (e) => {
    if (e.target && e.target.querySelector && e.target.querySelector('.st-reviews-mount')) init();
  });
})();
